import React from 'react'
import {
  Card,
  CardContent,
  Box,
  Typography,
  IconButton,
  useTheme,
  alpha,
  Skeleton,
  useMediaQuery,
} from '@mui/material'
import {
  ArrowUpward,
  ArrowDownward,
  MoreVert,
  Visibility,
  VisibilityOff,
} from '@mui/icons-material'

interface StatsCardProps {
  title: string
  value: string | number
  subtitle?: string
  icon?: React.ReactNode
  trend?: {
    value: string
    direction: 'up' | 'down' | 'neutral'
    label?: string
  }
  color?: 'primary' | 'secondary' | 'success' | 'error' | 'warning' | 'info'
  loading?: boolean
  hideValue?: boolean
  onToggleVisibility?: () => void
  onMenuClick?: (event: React.MouseEvent<HTMLElement>) => void
  onClick?: () => void
}

const StatsCard: React.FC<StatsCardProps> = ({
  title,
  value,
  subtitle,
  icon,
  trend,
  color = 'primary',
  loading = false,
  hideValue = false,
  onToggleVisibility,
  onMenuClick,
  onClick,
}) => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  if (loading) {
    return (
      <Card sx={{ height: '100%' }}>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 2 }}>
            <Skeleton variant="circular" width={isMobile ? 40 : 48} height={isMobile ? 40 : 48} />
            <Skeleton variant="circular" width={24} height={24} />
          </Box>
          <Skeleton variant="text" width="60%" height={20} sx={{ mb: 1 }} />
          <Skeleton variant="text" width="100%" height={isMobile ? 32 : 40} sx={{ mb: 1 }} />
          <Skeleton variant="text" width="40%" height={16} />
        </CardContent>
      </Card>
    )
  }

  const getTrendColor = () => {
    switch (trend?.direction) {
      case 'up':
        return theme.palette.success.main
      case 'down':
        return theme.palette.error.main
      default:
        return theme.palette.text.secondary
    }
  }

  const getTrendIcon = () => {
    switch (trend?.direction) {
      case 'up':
        return <ArrowUpward sx={{ fontSize: { xs: 14, sm: 16 } }} />
      case 'down':
        return <ArrowDownward sx={{ fontSize: { xs: 14, sm: 16 } }} />
      default:
        return null
    }
  }

  return (
    <Card
      sx={{
        height: '100%',
        cursor: onClick ? 'pointer' : 'default',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
        borderRadius: { xs: 2, sm: 3 },
        '&:hover': {
          transform: onClick ? 'translateY(-4px)' : 'translateY(-2px)',
          boxShadow: theme.shadows[8],
          borderColor: alpha(theme.palette[color].main, 0.3),
        },
        '&:active': onClick ? {
          transform: 'translateY(-1px) scale(0.98)',
        } : {},
        // Mobile touch optimizations
        '@media (hover: none)': {
          '&:hover': {
            transform: 'none',
            boxShadow: theme.shadows[2],
          },
          '&:active': {
            transform: 'scale(0.98)',
            boxShadow: theme.shadows[4],
          },
        },
      }}
      onClick={onClick}
    >
      <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
        {/* Header */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            mb: { xs: 1.5, sm: 2 },
          }}
        >
          {icon && (
            <Box
              sx={{
                p: { xs: 1, sm: 1.5 },
                borderRadius: { xs: 1.5, sm: 2 },
                bgcolor: alpha(theme.palette[color].main, 0.1),
                color: `${color}.main`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                minWidth: { xs: 40, sm: 48 },
                minHeight: { xs: 40, sm: 48 },
                boxShadow: `0 2px 8px ${alpha(theme.palette[color].main, 0.15)}`,
              }}
            >
              {React.cloneElement(icon as React.ReactElement, {
                sx: { fontSize: { xs: '1.2rem', sm: '1.5rem' } }
              })}
            </Box>
          )}
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            {onToggleVisibility && (
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation()
                  onToggleVisibility()
                }}
                sx={{
                  p: { xs: 0.5, sm: 1 },
                  borderRadius: 1.5,
                  '&:hover': {
                    backgroundColor: alpha(theme.palette.action.hover, 0.8),
                  },
                  '&:active': {
                    transform: 'scale(0.9)',
                  },
                }}
              >
                {hideValue ? 
                  <VisibilityOff sx={{ fontSize: { xs: '1rem', sm: '1.2rem' } }} /> : 
                  <Visibility sx={{ fontSize: { xs: '1rem', sm: '1.2rem' } }} />
                }
              </IconButton>
            )}
            {onMenuClick && (
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation()
                  onMenuClick(e)
                }}
                sx={{
                  p: { xs: 0.5, sm: 1 },
                  borderRadius: 1.5,
                  '&:hover': {
                    backgroundColor: alpha(theme.palette.action.hover, 0.8),
                  },
                  '&:active': {
                    transform: 'scale(0.9)',
                  },
                }}
              >
                <MoreVert sx={{ fontSize: { xs: '1rem', sm: '1.2rem' } }} />
              </IconButton>
            )}
          </Box>
        </Box>

        {/* Subtitle */}
        {subtitle && (
          <Typography
            variant="body2"
            color="text.secondary"
            gutterBottom
            sx={{ 
              fontWeight: 500,
              fontSize: { xs: '0.75rem', sm: '0.875rem' },
              mb: { xs: 0.5, sm: 1 },
            }}
          >
            {subtitle}
          </Typography>
        )}

        {/* Value */}
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mb: { xs: 0.5, sm: 1 } }}>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 700,
              color: 'text.primary',
              lineHeight: 1.2,
              fontSize: { xs: '1.5rem', sm: '2rem', md: '2.125rem' },
              wordBreak: 'break-all',
            }}
          >
            {hideValue ? '••••••' : value}
          </Typography>
        </Box>

        {/* Title */}
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{
            fontWeight: 500,
            mb: trend ? { xs: 0.5, sm: 1 } : 0,
            fontSize: { xs: '0.75rem', sm: '0.875rem' },
          }}
        >
          {title}
        </Typography>

        {/* Trend */}
        {trend && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                color: getTrendColor(),
                p: 0.5,
                borderRadius: 1,
                backgroundColor: alpha(getTrendColor(), 0.1),
              }}
            >
              {getTrendIcon()}
            </Box>
            <Typography
              variant="body2"
              sx={{
                color: getTrendColor(),
                fontWeight: 600,
                fontSize: { xs: '0.75rem', sm: '0.875rem' },
              }}
            >
              {trend.value}
            </Typography>
            {trend.label && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ 
                  ml: 0.5,
                  fontSize: { xs: '0.65rem', sm: '0.75rem' },
                }}
              >
                {trend.label}
              </Typography>
            )}
          </Box>
        )}
      </CardContent>
    </Card>
  )
}

export default StatsCard